import { env } from '@config/env';
import { authFailed, authSucceeded } from '@features/auth/authSlice';
import {
  type BackendAuthResponse,
  profileToUser,
  userFromAuthResponse,
  type UserProfileDTO,
} from '@features/auth/authTransforms';
import type { AuthTokens, User } from '@features/auth/types';
import { tokenStorage } from '@features/auth/utils/tokenStorage';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { withRetry } from './retry';
import { transformError } from './transformError';

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.VITE_API_URL,
  prepareHeaders: (headers) => {
    const token = tokenStorage.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

// Retry network + 5xx + 429 (only on idempotent methods). Refresh is handled
// outside this wrapper so 401 is never retried through this path.
const rawBaseQueryWithRetry = withRetry(rawBaseQuery, {
  maxAttempts: 3,
  baseDelayMs: 300,
  maxDelayMs: 8_000,
});

function tokensAndUserFromRefresh(data: BackendAuthResponse): { tokens: AuthTokens; user: User } {
  const tokens: AuthTokens = {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };

  return { tokens, user: userFromAuthResponse(data) };
}

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await rawBaseQueryWithRetry(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) {
          tokenStorage.clear();
          api.dispatch(authFailed(null));
          return result;
        }

        const refreshResult = await rawBaseQuery(
          {
            url: 'auth/refresh-token',
            method: 'POST',
            body: { refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          let tokens: AuthTokens;
          let user: User;
          try {
            ({ tokens, user } = tokensAndUserFromRefresh(
              refreshResult.data as BackendAuthResponse,
            ));
          } catch {
            tokenStorage.clear();
            api.dispatch(authFailed(null));
            return result;
          }
          // Preserve the user's original "remember me" choice across refresh.
          const remember = tokenStorage.isPersistent() ?? true;
          tokenStorage.setTokens(tokens, { remember });
          api.dispatch(authSucceeded(user));
          const profileResult = await rawBaseQuery(
            { url: 'users/profile', method: 'GET' },
            api,
            extraOptions,
          );
          if (profileResult.data) {
            api.dispatch(
              authSucceeded(profileToUser(profileResult.data as UserProfileDTO)),
            );
          }
          result = await rawBaseQueryWithRetry(args, api, extraOptions);
        } else {
          tokenStorage.clear();
          api.dispatch(
            authFailed(refreshResult.error ? transformError(refreshResult.error) : null),
          );
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQueryWithRetry(args, api, extraOptions);
    }
  }

  return result;
};
